import { useEffect, useRef } from 'react';
import {
  setupHandshakeListener,
  calculateContentHeight,
  sendHeightToParent,
  sendScrollToTop,
  registerParentConnection
} from '../utils/iframeMessenger';

const MAX_ELEMENT_WAIT_TIME = 5000;
const RETRY_INTERVAL = 100;
const MAX_RETRIES = MAX_ELEMENT_WAIT_TIME / RETRY_INTERVAL;
const HEIGHT_CHANGE_THRESHOLD = 5;
const DEBOUNCE_DELAY = 150; // 150ms - wait for transitions to complete

/**
 * Automatic iframe height synchronization
 *
 * Sequential process (all in one useEffect):
 * 1. Check if in iframe → if not, stop
 * 2. Setup handshake listener → wait for parent
 * 3. When handshake received → find element (with retry)
 * 4. When element found → setup height monitoring
 */
export const useIframeHeightSync = (
  elementSelector: string = '.App'
): void => {
  const lastHeightRef = useRef<number>(0);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const routeChangeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    // KROK 1: Sprawdź czy w iframe
    const isInIframe = window.self !== window.top;
    if (!isInIframe) return;

    // Store cleanup functions
    let cleanupRouteListeners: (() => void) | null = null;

    // KROK 2: Czekaj na handshake od parenta
    const removeListener = setupHandshakeListener((parentOrigin, widgetId) => {
      registerParentConnection(parentOrigin, widgetId);

      // KROK 3: Szukaj element (z retry)
      let retryCount = 0;
      let currentElement: HTMLElement | null = null;

      const findElement = () => {
        const element = document.querySelector<HTMLElement>(elementSelector);

        if (element) {
          // KROK 4: Element znaleziony → setup monitoring
          currentElement = element;
          setupMonitoring(element, parentOrigin, widgetId);

          // KROK 5: Setup route change listener (dla React Router SPA navigation)
          const handleRouteChange = () => {
            // Clear any pending route change timers from previous navigation
            routeChangeTimersRef.current.forEach(timer => clearTimeout(timer));
            routeChangeTimersRef.current = [];

            // Scroll parent page to iframe top
            sendScrollToTop(parentOrigin, widgetId);

            // Multi-retry mechanism - images load asynchronously
            // Send height multiple times until it stabilizes
            const delays = [100, 500, 1000, 2000];

            delays.forEach((delay) => {
              const timer = setTimeout(() => {
                const newHeight = calculateContentHeight(element);
                sendHeightToParent(newHeight, parentOrigin, widgetId);
              }, delay);
              routeChangeTimersRef.current.push(timer);
            });
          };

          // Nasłuchuj popstate (React Router back/forward)
          window.addEventListener('popstate', handleRouteChange);

          // Nasłuchuj pushstate/replacestate (programmatic navigation)
          const originalPushState = window.history.pushState;
          const originalReplaceState = window.history.replaceState;

          window.history.pushState = function(...args) {
            originalPushState.apply(this, args);
            handleRouteChange();
          };

          window.history.replaceState = function(...args) {
            originalReplaceState.apply(this, args);
            handleRouteChange();
          };

          cleanupRouteListeners = () => {
            window.removeEventListener('popstate', handleRouteChange);
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
          };

          return;
        }

        // Element nie znaleziony → retry
        if (retryCount >= MAX_RETRIES) {
          console.error(
            `[TwoPoint iframe] Element "${elementSelector}" not found after ${MAX_ELEMENT_WAIT_TIME}ms`
          );
          return;
        }

        retryCount++;
        setTimeout(findElement, RETRY_INTERVAL);
      };

      // KROK 3: Start szukania
      findElement();
    });

    // Funkcja do monitorowania i wysyłania wysokości
    const setupMonitoring = (
      element: HTMLElement,
      parentOrigin: string,
      widgetId: string | null
    ) => {
      const sendHeight = () => {
        const newHeight = calculateContentHeight(element);
        const heightDiff = Math.abs(newHeight - lastHeightRef.current);

        if (heightDiff >= HEIGHT_CHANGE_THRESHOLD) {
          sendHeightToParent(newHeight, parentOrigin, widgetId);
          lastHeightRef.current = newHeight;
        }
      };

      const debouncedSendHeight = () => {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(sendHeight, DEBOUNCE_DELAY);
      };

      // Initial height
      sendHeight();

      // MutationObserver - wykrywa zmiany DOM (dodawanie/usuwanie elementów, class, style)
      mutationObserverRef.current = new MutationObserver(debouncedSendHeight);
      mutationObserverRef.current.observe(element, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });

      // ResizeObserver - wykrywa zmiany rozmiaru elementu (media queries, layout reflow)
      // Triggeruje przy KAŻDEJ zmianie rozmiaru (nawet podczas transition)
      resizeObserverRef.current = new ResizeObserver((entries) => {
        // Ignore initial trigger with size 0
        for (const entry of entries) {
          if (entry.contentRect.height > 0) {
            debouncedSendHeight();
          }
        }
      });
      resizeObserverRef.current.observe(element);
    };

    // Cleanup
    return () => {
      removeListener();
      mutationObserverRef.current?.disconnect();
      resizeObserverRef.current?.disconnect();
      clearTimeout(debounceTimerRef.current);
      routeChangeTimersRef.current.forEach(timer => clearTimeout(timer));
      cleanupRouteListeners?.();
    };
  }, [elementSelector]);
};
